import React, { useState, useEffect, useContext } from 'react';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Button,
  Badge,
} from '@mui/material';
import RedeemIcon from '@mui/icons-material/Redeem';
import CloseIcon from '@mui/icons-material/Close';

import { alpha, styled, keyframes } from '@mui/material/styles';
import { UserContext } from '../contexts/UserContext';
import { auth } from '../firebase';

// ---- GOLD THEME TOKENS ----
const GOLD = '#d4af37';
const TEXT = '#f9f6e3';
const PANEL_BG = 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)';

// ---- Gold pulse animation for ICON (not badge) ----
const iconPulse = keyframes`
  0% {
    box-shadow: 0 0 0 rgba(212,175,55,0.0);
  }
  50% {
    box-shadow:
      0 0 18px rgba(212,175,55,0.65),
      0 0 36px rgba(212,175,55,0.4);
  }
  100% {
    box-shadow: 0 0 0 rgba(212,175,55,0.0);
  }
`;

// ---- Pulsing Icon Button (scaled) ----
const PulsingIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'pulse',
})(({ pulse }) => ({
  transform: 'scale(1.5)', // ~50% larger (always)
  ...(pulse && {
    animation: `${iconPulse} 1.8s ease-in-out infinite`,
  }),
}));

// ---- Static Alert Badge (NO pulse) ----
const AlertBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#ff3b3b',
    color: '#fff',
    fontSize: 14,
    fontWeight: 900,
    minWidth: 22,
    height: 22,
    borderRadius: '50%',
    border: '2px solid #000',
    boxShadow: '0 0 8px rgba(255,59,59,0.95)',
  },
}));

// ---- Gold Button ----
const GoldButton = styled(Button)(() => ({
  fontWeight: 700,
  letterSpacing: '.6px',
  color: TEXT,
  borderRadius: 12,
  padding: '10px 14px',
  background: `linear-gradient(180deg, ${alpha(GOLD, 0.9)} 0%, ${alpha(
    GOLD,
    0.6
  )} 100%)`,
  boxShadow: `0 8px 24px ${alpha(GOLD, 0.25)}`,
  textTransform: 'none',
  '&:hover': {
    boxShadow: `0 10px 28px ${alpha(GOLD, 0.35)}`,
    filter: 'brightness(1.05)',
    background: `linear-gradient(180deg, ${alpha(GOLD, 1)} 0%, ${alpha(
      GOLD,
      0.75
    )} 100%)`,
  },
  '&:disabled': {
    color: alpha(TEXT, 0.6),
    background: alpha(GOLD, 0.25),
    boxShadow: 'none',
  },
}));

// ---- Helper: format countdown ----
function formatCountdown(ms) {
  if (ms == null || !Number.isFinite(ms)) return '--:--:--';
  const sec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(
    2,
    '0'
  )}:${String(s).padStart(2, '0')}`;
}

// ---- Helpers to mirror server logic (Sunday + last day of month) ----
function isSunday(date) {
  return date.getDay() === 0;
}

function isLastDayOfMonth(date) {
  const nextDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + 1
  );
  return nextDay.getDate() === 1;
}

// Normalize Firestore Timestamp or number -> number (ms) or null
function normalizeTs(raw) {
  if (!raw) return null;
  if (typeof raw === 'number') return raw;
  if (typeof raw.toMillis === 'function') return raw.toMillis();
  return null;
}

export default function DailyRewards() {
  const { userData } = useContext(UserContext);
  const [open, setOpen] = useState(false);

  const [dailyNextAt, setDailyNextAt] = useState(null);
  const [weeklyNextAt, setWeeklyNextAt] = useState(null);
  const [monthlyNextAt, setMonthlyNextAt] = useState(null);

  const [now, setNow] = useState(Date.now());
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);

  // Pull reward timers from Firestore user document (calendarRewards)
  useEffect(() => {
    if (!userData?.calendarRewards) return;
    const { dailyNextAt, weeklyNextAt, monthlyNextAt } =
      userData.calendarRewards;

    setDailyNextAt(normalizeTs(dailyNextAt));
    setWeeklyNextAt(normalizeTs(weeklyNextAt));
    setMonthlyNextAt(normalizeTs(monthlyNextAt));
  }, [userData]);

  // live ticking clock for countdowns
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const nowDate = new Date(now);

  // ✅ MATCH CLOUD FUNCTION EXACTLY (no functional changes)
  // Server logic:
  // dailyReady = !dailyNextMs || nowMs >= dailyNextMs
  // weeklyReady = (!weeklyNextMs || nowMs >= weeklyNextMs) && isSunday(nowDate)
  // monthlyReady = (!monthlyNextMs || nowMs >= monthlyNextMs) && isLastDayOfMonth(nowDate)
  const dailyReady = !dailyNextAt || dailyNextAt <= now;
  const weeklyReady = (!weeklyNextAt || weeklyNextAt <= now) && isSunday(nowDate);
  const monthlyReady =
    (!monthlyNextAt || monthlyNextAt <= now) && isLastDayOfMonth(nowDate);

  const anyReady = dailyReady || weeklyReady || monthlyReady;

  async function handleClaim() {
    if (!auth.currentUser) return;

    try {
      setClaiming(true);
      setError(null);

      const token = await auth.currentUser.getIdToken();

      const res = await fetch(
        'https://claimcalendarreward-qfihkipuza-uc.a.run.app',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error('Unexpected response from server');
      }

      if (!res.ok || data.error) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      // Server returns numbers in ms or null
      setDailyNextAt(data.dailyNextAt ?? null);
      setWeeklyNextAt(data.weeklyNextAt ?? null);
      setMonthlyNextAt(data.monthlyNextAt ?? null);
    } catch (e) {
      setError(e.message || 'Failed to claim rewards');
    } finally {
      setClaiming(false);
    }
  }

  // Helpers to render the chips nicely and safely
  function renderDailyChip() {
    if (dailyReady) {
      return (
        <Chip
          label="Ready to Claim!"
          sx={{
            color: TEXT,
            background: alpha(GOLD, 0.2),
            borderColor: GOLD,
            mb: 2,
          }}
        />
      );
    }

    if (dailyNextAt && dailyNextAt > now) {
      return (
        <Chip
          label={'Ready in ' + formatCountdown(dailyNextAt - now)}
          sx={{
            color: TEXT,
            borderColor: GOLD,
            background: alpha(GOLD, 0.1),
            mb: 2,
          }}
        />
      );
    }

    return <Chip label="Not yet available" sx={{ color: TEXT, mb: 2 }} />;
  }

  function renderWeeklyChip() {
    if (weeklyReady) {
      return (
        <Chip
          label="Ready to Claim!"
          sx={{
            color: TEXT,
            background: alpha(GOLD, 0.2),
            borderColor: GOLD,
            mb: 2,
          }}
        />
      );
    }

    if (weeklyNextAt && weeklyNextAt > now) {
      return (
        <Chip
          label={'Ready in ' + formatCountdown(weeklyNextAt - now)}
          sx={{ color: TEXT, mb: 2 }}
        />
      );
    }

    // No scheduled nextAt or it's past but not Sunday
    return <Chip label="Not available today" sx={{ color: TEXT, mb: 2 }} />;
  }

  function renderMonthlyChip() {
    if (monthlyReady) {
      return (
        <Chip
          label="Ready to Claim!"
          sx={{
            color: TEXT,
            background: alpha(GOLD, 0.2),
            borderColor: GOLD,
            mb: 2,
          }}
        />
      );
    }

    if (monthlyNextAt && monthlyNextAt > now) {
      return (
        <Chip
          label={'Ready in ' + formatCountdown(monthlyNextAt - now)}
          sx={{ color: TEXT, mb: 2 }}
        />
      );
    }

    return <Chip label="Not available today" sx={{ color: TEXT, mb: 2 }} />;
  }

  return (
    <>
      {/* ICON (style only changes: pulse + badge + scale) */}
      <Tooltip title="Daily/Weekly/Monthly Rewards">
        <AlertBadge
          invisible={!anyReady}
          badgeContent="!"
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ mr: 1 }}
        >
          <PulsingIconButton
            pulse={anyReady}
            onClick={() => setOpen(true)}
            sx={{
              color: GOLD,
              background: alpha('#000', 0.5),
              border: `1px solid ${alpha(GOLD, 0.4)}`,
              boxShadow: `0 0 12px ${alpha(GOLD, 0.4)}`,
              '&:hover': {
                background: alpha(GOLD, 0.15),
              },
            }}
          >
            <RedeemIcon sx={{ fontSize: 28 }} />
          </PulsingIconButton>
        </AlertBadge>
      </Tooltip>

      {/* REWARD WINDOW */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: PANEL_BG,
            border: `1px solid ${alpha(GOLD, 0.3)}`,
            boxShadow: `0 0 0 1px ${alpha(GOLD, 0.2)} inset, 0 15px 35px ${alpha(
              GOLD,
              0.25
            )}`,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: TEXT,
            fontFamily: `'Cinzel', serif`,
            fontWeight: 700,
            letterSpacing: '.8px',
            borderBottom: `1px solid ${alpha(GOLD, 0.3)}`,
          }}
        >
          Daily / Weekly / Monthly Rewards
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon sx={{ color: GOLD }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {/* DAILY */}
          <Typography sx={{ color: GOLD, fontFamily: `'Cinzel', serif`, mb: 0.5 }}>
            Daily Reward
          </Typography>
          <Typography sx={{ color: TEXT, mb: 1 }}>
            • 1× Basic Free Draw Ticket • 1× Soul Dust
          </Typography>
          {renderDailyChip()}

          {/* WEEKLY */}
          <Typography sx={{ color: GOLD, fontFamily: `'Cinzel', serif`, mb: 0.5 }}>
            Weekly Reward (Sunday)
          </Typography>
          <Typography sx={{ color: TEXT, mb: 1 }}>
            • 5× Basic Free Draw Ticket • 5× Soul Dust
          </Typography>
          {renderWeeklyChip()}

          {/* MONTHLY */}
          <Typography sx={{ color: GOLD, fontFamily: `'Cinzel', serif`, mb: 0.5 }}>
            Monthly Reward (last day of month)
          </Typography>
          <Typography sx={{ color: TEXT, mb: 1 }}>
            • 10× Basic Free Draw Ticket • 10× Soul Dust
          </Typography>
          {renderMonthlyChip()}

          {error && (
            <Typography sx={{ color: '#ff6b6b', mt: 1 }}>
              Error: {error}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: `1px solid ${alpha(GOLD, 0.3)}` }}>
          <GoldButton
            disabled={claiming || !anyReady || !auth.currentUser}
            onClick={handleClaim}
          >
            {claiming ? 'Claiming…' : 'Claim Rewards'}
          </GoldButton>
          <Button onClick={() => setOpen(false)} sx={{ color: TEXT }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}